<template>
  <div class="activity-detail-page">
    <SubPageHeader :title="detail ? detail.name : '活动详情'" />

    <div v-if="loading" class="state-text">加载中...</div>
    <div v-else-if="!detail" class="state-text">暂无数据</div>

    <template v-else>
      <!-- 封面图 -->
      <div class="cover-img" v-if="getImgUrl(detail.imgUrl)">
        <img :src="getImgUrl(detail.imgUrl)" alt="封面" />
      </div>

      <!-- 基本信息 -->
      <div class="section">
        <div class="section-title">基本信息</div>
        <div class="info-card">
          <div class="info-row">
            <span class="label">活动名称</span>
            <span class="value">{{ detail.name }}</span>
          </div>
          <div class="info-row">
            <span class="label">发布组织</span>
            <span class="value">{{ detail.orgName }}</span>
          </div>
          <div class="info-row">
            <span class="label">活动类型</span>
            <span class="value">{{ getTypeName(detail.type) }}</span>
          </div>
          <div class="info-row">
            <span class="label">活动状态</span>
            <span class="value" :style="{ color: getStatusColor(detail.dateComparison) }">
              {{ getStatusName(detail.dateComparison) }}
            </span>
          </div>
          <div class="info-row">
            <span class="label">活动时间</span>
            <span class="value">{{ detail.activityStartTime || detail.actStartTime }}~{{ detail.activityEndTime || detail.actEndTime }}</span>
          </div>
          <template v-if="detail.type !== 1">
            <div class="info-row">
              <span class="label">报名时间</span>
              <span class="value">{{ detail.signStartTime }}~{{ detail.signEndTime }}</span>
            </div>
            <div class="info-row">
              <span class="label">报名人数</span>
              <span class="value">{{ detail.singCount || 0 }}</span>
            </div>
          </template>
          <div class="info-row">
            <span class="label">参与人数</span>
            <span class="value">{{ detail.userCount || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">签到人数</span>
            <span class="value">{{ detail.userSingFlagCount || 0 }}</span>
          </div>
          <div class="info-row" v-if="detail.remark">
            <span class="label">备注</span>
            <span class="value">{{ detail.remark }}</span>
          </div>
          <div class="info-row" v-if="detail.recordInfo">
            <span class="label">活动情况</span>
            <span class="value">{{ detail.recordInfo }}</span>
          </div>
          <!-- 附件 -->
          <div class="info-row" v-if="attachments.length > 0">
            <span class="label">附件</span>
            <div class="value attachments">
              <a
                v-for="(file, idx) in attachments"
                :key="idx"
                :href="file.url"
                target="_blank"
                download
                class="attachment-item"
              >
                <span class="attachment-icon">{{ getFileIcon(file.ext) }}</span>
                <span class="attachment-name">{{ file.name }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- 我的参与状态 -->
      <div class="section">
        <div class="section-title">我的状态</div>
        <div class="info-card">
          <div class="info-row">
            <span class="label">参与状态</span>
            <span class="value sign-badge" :class="signFlagClass(detail.signFlag)">
              {{ getSignFlagName(detail.signFlag) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import SubPageHeader from '../component/SubPageHeader.vue';
import { getActivityDetail } from '@/api';

const route = useRoute();
const loading = ref(false);
const detail = ref(null);

const getImgUrl = (imgUrlStr) => {
  if (!imgUrlStr) return '';
  try {
    const arr = JSON.parse(imgUrlStr);
    if (Array.isArray(arr) && arr.length > 0) return arr[0].url || '';
  } catch (e) {
    // not json, use as plain url
    return imgUrlStr;
  }
  return '';
};

const getTypeName = (type) => {
  const map = { 1: '组织活动', 2: '志愿活动', 3: '投票活动', 4: '调查问卷' };
  return map[type] || '未知';
};

const getStatusName = (dateComparison) => {
  const map = { 1: '待开始', 2: '进行中', 3: '已结束' };
  return map[dateComparison] || '-';
};

const getStatusColor = (dateComparison) => {
  if (dateComparison === 2) return '#2b85e4';
  if (dateComparison === 3) return '#999';
  return '#f5a623';
};

const getSignFlagName = (signFlag) => {
  const map = { 0: '未参与', 1: '已报名，未签到', 2: '已签到' };
  return map[signFlag] ?? '-';
};

const signFlagClass = (signFlag) => {
  if (signFlag === 2) return 'signed';
  if (signFlag === 1) return 'registered';
  return 'not-joined';
};

const attachments = ref([]);

const parseAttachments = (str) => {
  if (!str) return [];
  try {
    const arr = JSON.parse(str);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};

const getFileIcon = (ext) => {
  const iconMap = {
    pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
    ppt: '📋', pptx: '📋', zip: '🗜', rar: '🗜',
    png: '🖼', jpg: '🖼', jpeg: '🖼', gif: '🖼', mp4: '🎬', mp3: '🎵'
  };
  return iconMap[(ext || '').toLowerCase()] || '📎';
};

const loadDetail = async () => {
  loading.value = true;
  try {
    const res = await getActivityDetail({
      objId: route.query.objId,
      type: route.query.type
    });
    if (res.stat === 1 || res.isSuccess) {
      detail.value = res.data;
      attachments.value = parseAttachments(res.data.attachmentFile);
    }
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadDetail();
});
</script>

<style scoped lang="scss">
.activity-detail-page {
  min-height: 100vh;
  background: #f4f6f8;
  padding-bottom: 20px;
  box-sizing: border-box;
}

.state-text {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
}

.cover-img {
  width: 100%;
  max-height: 200px;
  overflow: hidden;
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
}

.section {
  margin: 12px 15px 0;

  .section-title {
    font-size: 15px;
    font-weight: bold;
    color: #333;
    padding: 10px 0 8px;
    border-bottom: 2px solid #e02020;
    margin-bottom: 0;
    display: inline-block;
  }
}

.info-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 15px;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  .label {
    flex-shrink: 0;
    width: 80px;
    font-size: 14px;
    color: #999;
  }

  .value {
    flex: 1;
    font-size: 14px;
    color: #333;
    word-break: break-all;
  }

  .attachments {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #f5f7fa;
    border-radius: 6px;
    text-decoration: none;
    color: #2b85e4;
    font-size: 13px;
    word-break: break-all;

    .attachment-icon {
      font-size: 16px;
      flex-shrink: 0;
    }

    .attachment-name {
      flex: 1;
      text-decoration: underline;
    }

    &:active {
      background: #e8f3ff;
    }
  }

  .sign-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 13px;

    &.not-joined {
      color: #999;
      background: #f5f5f5;
    }
    &.registered {
      color: #f5a623;
      background: #fff8ee;
    }
    &.signed {
      color: #2b85e4;
      background: #e8f3ff;
    }
  }
}
</style>
